/* Diamond Studio — homepage CRO helpers (2026-09-29)
   1. Hero: the live store rating next to the buttons, and "How offers work" in three short steps.
   2. Service bar: one concrete line under each promise (free worldwide shipping, 60-day returns, ...).
   3. "Pick up where you left off": recently viewed pieces for returning shoppers (one per design).
   4. Studio edit cards: the same "30% OFF" / "Free diamond studs" badges as collection cards, plus estimated arrival.
   5. Gift finder: the free studs offer next to the budget picker, and a tag on picks that qualify.
   6. Customer clips: three written customer reviews, the store rating and a "Shop engagement rings" button.
   7. "Still in your bag": returning shoppers with pieces in the bag see them near the top, with gift status.
   8. "Good to know before you make an offer": the store's own answers on offers, replies, delivery and returns.
   9. Concierge band: a second way in ("Or talk to the studio") next to the finder button.
  10. Desktop only, once per session: when the pointer leaves the page, a small card with the piece in the bag,
      else the last piece viewed ("Make an offer"), else the "Find my match" quiz.
  11. "Find my match" results: "Make an offer" under each piece, the "% OFF" and free studs tags, and
      right-sized photos (they were loading 900px images for 85px thumbnails). The gift picks get the same photos.
  12. Measurement: the finder funnel (opened, results shown, piece or offer chosen) goes to the dataLayer.
   Everything is idempotent, because ds-home.js renders most sections itself; additions are applied in a
   microtask right after each render so they land before the browser paints (no layout shift). */
(() => {
  'use strict';
  if (window.DSHomeCRO) return;
  window.DSHomeCRO = {version: '2026-09-29'};

  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  const track = (placement, extra) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({event: 'ds_home_cro_click', placement}, extra || {}));
  };
  const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const currency = () => window.DS_CRO?.currency || 'USD';
  const money = cents => {
    const cur = currency();
    const amount = (cents / 100).toLocaleString('en-US', {minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2});
    return (cur === 'USD' ? '$' : '') + amount + (window.DS_CRO?.country === 'US' && cur === 'USD' ? '' : ' ' + cur);
  };
  const toCents = text => {
    const n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  };
  const ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h16m-6-6 6 6-6 6"/></svg>';
  const GIFT = '<svg class="ds-hc-gift-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="8.5" width="17" height="4" rx=".5"/><path d="M5 12.5v8h14v-8M12 8.5v12M12 8.5c-1.5-3.5-5.5-4-5.5-1.5 0 1.5 2.5 1.5 5.5 1.5Zm0 0c1.5-3.5 5.5-4 5.5-1.5 0 1.5-2.5 1.5-5.5 1.5Z"/></svg>';

  /* ---------- Shared data ---------- */
  function rating() {
    const el = document.getElementById('ds-store-rating-data');
    if (!el) return null;
    try {
      const data = JSON.parse(el.textContent);
      const count = Number(data.count) || 0;
      const value = Number(data.rating) || 0;
      if (count < 1 || value <= 0) return null;
      return {count, value, text: value.toFixed(1), word: count === 1 ? 'review' : 'reviews'};
    } catch (e) { return null; }
  }
  function ratingButton(variant, labelFn) {
    const r = rating();
    if (!r) return null;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ds-hc-rating ds-hc-rating--' + variant;
    b.setAttribute('data-ds-sr-open', '');
    b.setAttribute('aria-haspopup', 'dialog');
    b.setAttribute('aria-label', 'Rated ' + r.text + ' out of 5 from ' + r.count + ' customer ' + r.word + '. Read the reviews');
    b.innerHTML = '<span class="ds-sr-stars" style="--ds-sr: ' + r.value + '" aria-hidden="true"></span>' +
      '<span class="ds-hc-rating__text">' + (labelFn ? labelFn(r) : '<strong>' + r.text + '</strong> &middot; ' + r.count + ' customer ' + r.word) + '</span>';
    b.addEventListener('click', () => track('rating_' + variant));
    return b;
  }
  const giftConfig = () => window.DSStudGiftConfig || {threshold: 300000, inclusive: true, currency: 'USD'};
  const giftOn = () => currency() === (giftConfig().currency || 'USD');
  const qualifies = cents => {
    const g = giftConfig();
    if (!giftOn() || !cents) return false;
    return g.inclusive === false ? cents > g.threshold : cents >= g.threshold;
  };
  const giftThresholdText = () => 'US$' + (giftConfig().threshold / 100).toLocaleString('en-US');
  // Product pages open the offer form straight away with ?intent=offer (same links as the collection cards).
  const offerUrl = url => url + (url.includes('?') ? '&' : '?') + 'intent=offer';
  // Shopify CDN photos: ask for at most `max` pixels wide.
  const lighten = (img, max) => {
    const src = img?.getAttribute('src');
    if (!src) return;
    const next = src.replace(/([?&]width=)(\d+)/, (m, key, n) => key + Math.min(Number(n), max));
    if (next !== src) img.setAttribute('src', next);
  };
  const giftTag = () => {
    const tag = document.createElement('em');
    tag.className = 'ds-hc-pick-gift';
    tag.innerHTML = GIFT + 'Includes free diamond studs';
    return tag;
  };
  const eta = () => {
    if (window.DSCardCRO?.etaText) return window.DSCardCRO.etaText;
    const fmt = new Intl.DateTimeFormat('en-US', {month: 'short', day: 'numeric'});
    const add = n => { const d = new Date(); d.setDate(d.getDate() + n); return d; };
    const a = add(14), b = add(18);
    return 'Est. arrival ' + (a.getMonth() === b.getMonth() ? fmt.format(a) + '–' + b.getDate() : fmt.format(a) + ' – ' + fmt.format(b));
  };

  /* ---------- 1. Hero ---------- */
  // The rating and "How offers work" are rendered in templates/index.liquid so the hero never jumps.
  // This only adds them if that markup is missing (older template), and binds click tracking once.
  function hero() {
    const copy = document.querySelector('#hero .opening-copy');
    const actions = copy?.querySelector('.opening-actions');
    if (!copy || !actions || copy.hasAttribute('data-ds-hc')) return;
    copy.setAttribute('data-ds-hc', '');
    if (!copy.querySelector('.ds-hc-proof')) {
      const badge = ratingButton('hero');
      if (badge) {
        const proof = document.createElement('div');
        proof.className = 'ds-hc-proof';
        proof.append(badge);
        actions.after(proof);
      }
    } else {
      copy.querySelector('.ds-hc-rating')?.addEventListener('click', () => track('rating_hero'));
    }
    if (!copy.querySelector('.ds-hc-steps')) {
      const steps = document.createElement('div');
      steps.className = 'ds-hc-steps';
      steps.innerHTML = '<p class="ds-hc-steps__title" id="ds-hc-steps-title">How offers work</p>' +
        '<ol class="ds-hc-steps__list" role="list" aria-labelledby="ds-hc-steps-title">' +
        '<li><span class="ds-hc-steps__n" aria-hidden="true">1</span><span class="ds-hc-steps__text"><strong>Choose a piece</strong><span>Made new for you</span></span></li>' +
        '<li><span class="ds-hc-steps__n" aria-hidden="true">2</span><span class="ds-hc-steps__text"><strong>Name your price</strong><span>No payment to send an offer</span></span></li>' +
        '<li><span class="ds-hc-steps__n" aria-hidden="true">3</span><span class="ds-hc-steps__text"><strong>Hear back</strong><span>If the studio accepts, check out at that price</span></span></li>' +
        '</ol>';
      copy.append(steps);
    }
    // ds-home.js re-renders .opening-actions, so listen on the copy block (which it keeps).
    copy.addEventListener('click', event => {
      const link = event.target.closest('.opening-actions a, .opening-actions button');
      if (!link) return;
      if (link.hasAttribute('data-open-finder')) track('hero_finder');
      else track(link.classList.contains('btn') ? 'hero_primary' : 'hero_secondary');
    });
  }
  // "Find my match" next to "Explore all jewelry". It is also in templates/index.liquid for the first paint,
  // but ds-home.js rebuilds the buttons row, so it is put back here (before the next paint).
  function heroFinder() {
    const actions = document.querySelector('#hero .opening-actions');
    if (!actions || actions.querySelector('.ds-hc-finder-link')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'text-link ds-home-secondary-link ds-hc-finder-link';
    button.setAttribute('data-open-finder', '');
    button.setAttribute('aria-haspopup', 'dialog');
    button.innerHTML = 'Find my match ' + ARROW;
    actions.append(button);
  }
  // The main button's wording comes from the theme's language file (ds_home.hero.rings, printed into #ds-hc-copy
  // by templates/index.liquid). ds-home.js rebuilds the hero with its own fixed wording, so the text is set back here.
  let heroCopy;
  function heroLabel() {
    if (heroCopy === undefined) {
      try { heroCopy = JSON.parse(document.getElementById('ds-hc-copy')?.textContent || 'null'); } catch (e) { heroCopy = null; }
    }
    const label = String(heroCopy?.rings || '').trim();
    const button = document.querySelector('#hero .opening-actions a.btn');
    if (!label || !button) return;
    const text = [...button.childNodes].find(n => n.nodeType === 3 && n.textContent.trim());
    if (text && text.textContent.trim() !== label) text.textContent = label;
  }

  /* ---------- 2. Service bar ---------- */
  const SERVICE_LINES = [
    [/diamond-guide/, 'How they compare'],
    [/shipping/, 'Free worldwide shipping'],
    [/return/, '60-day returns'],
    [/contact/, 'Request a consultation']
  ];
  function services() {
    document.querySelectorAll('.store-services > a:not([data-ds-hc])').forEach(link => {
      const match = SERVICE_LINES.find(([re]) => re.test(link.pathname));
      const label = link.querySelector(':scope > span');
      if (!match || !label) return;
      link.setAttribute('data-ds-hc', '');
      const wrap = document.createElement('span');
      wrap.className = 'ds-hc-svc';
      label.before(wrap);
      wrap.append(label);
      const line = document.createElement('small');
      line.textContent = match[1];
      wrap.append(line);
      link.addEventListener('click', () => track('service_' + match[1].toLowerCase().replace(/[^a-z0-9]+/g, '_')));
    });
  }

  /* ---------- 3. Pick up where you left off ---------- */
  // The row is inserted straight away with placeholder tiles of the final size, then filled in,
  // so the sections below do not jump when the product data arrives.
  let recentStarted = false;
  let recentItems = [];
  const familyKey = h => h.replace(/(^|-)\d+(?:-\d{1,2})?-(?:carat|ct)(?=-|$)/, '$1').replace(/-(?:vs1|vs2|vvs1|vvs2)$/, '');
  const recentTile = p => {
    if (!p) return '<li class="ds-hc-recent__ghost" aria-hidden="true"><span class="ds-hc-recent__img"></span><span class="ds-hc-recent__name">&nbsp;<br>&nbsp;</span><span class="ds-hc-recent__price">&nbsp;</span><span class="ds-hc-recent__offer">&nbsp;</span></li>';
    const img = src => { const base = src.startsWith('//') ? 'https:' + src : src; return base + (base.includes('?') ? '&' : '?') + 'width=360'; };
    const was = p.compare_at_price > p.price ? '<del>' + esc(money(p.compare_at_price)) + '</del>' : '';
    return '<li><a href="' + esc(p.url) + '" data-ds-hc-recent><span class="ds-hc-recent__img"><img src="' + esc(img(p.featured_image)) + '" alt="" width="180" height="180" loading="lazy"></span>' +
      '<span class="ds-hc-recent__name">' + esc(p.title) + '</span><span class="ds-hc-recent__price"><strong>' + esc(money(p.price)) + '</strong>' + was + '</span></a>' +
      '<a class="ds-hc-recent__offer" href="' + esc(offerUrl(p.url)) + '" data-ds-hc-recent-offer aria-label="Make an offer on ' + esc(p.title) + '">Make an offer ' + ARROW + '</a></li>';
  };
  function recent() {
    const anchor = document.getElementById('collections');
    if (recentStarted || !anchor) return;
    recentStarted = true;
    let list = [];
    try { list = JSON.parse(localStorage.getItem('ds-recently-viewed-v1') || '[]'); } catch (e) { return; }
    if (!Array.isArray(list)) return;
    const seen = new Set();
    const handles = list.filter(item => item && item.h && Date.now() - item.t < 1000 * 60 * 60 * 24 * 60)
      .map(item => item.h)
      .filter(h => { const k = familyKey(h); if (seen.has(k)) return false; seen.add(k); return true; })
      .slice(0, 8);
    if (!handles.length) return;
    const section = document.createElement('section');
    section.className = 'ds-hc-recent';
    section.setAttribute('aria-labelledby', 'ds-hc-recent-title');
    section.setAttribute('aria-busy', 'true');
    section.innerHTML = '<div class="ds-hc-recent__head"><div><span class="kicker">Welcome back</span><h2 id="ds-hc-recent-title">Pick up where you left off</h2></div>' +
      '<button type="button" class="ds-hc-link ds-hc-recent__clear" aria-label="Clear recently viewed pieces">Clear</button></div>' +
      '<ul class="ds-hc-recent__list" role="list">' + Array.from({length: Math.min(handles.length, 6)}, () => recentTile(null)).join('') + '</ul>';
    section.addEventListener('click', event => {
      if (event.target.closest('[data-ds-hc-recent-offer]')) track('recently_viewed_offer');
      else if (event.target.closest('[data-ds-hc-recent]')) track('recently_viewed');
      else if (event.target.closest('.ds-hc-recent__clear')) {
        try { localStorage.removeItem('ds-recently-viewed-v1'); } catch (e) { /* storage unavailable */ }
        track('recently_viewed_clear');
        section.remove();
      }
    });
    anchor.before(section);
    Promise.allSettled(handles.map(h => fetch(root() + 'products/' + encodeURIComponent(h) + '.js', {credentials: 'same-origin'}).then(r => r.ok ? r.json() : null)))
      .then(results => {
        const items = results.map(r => r.status === 'fulfilled' ? r.value : null).filter(p => p && p.available && p.featured_image).slice(0, 6);
        if (!items.length) { section.remove(); return; }
        recentItems = items;
        section.querySelector('.ds-hc-recent__list').innerHTML = items.map(recentTile).join('');
        section.removeAttribute('aria-busy');
      });
  }

  /* ---------- 4. Studio edit cards ---------- */
  // Same badges as the collection cards (snippets/ds-collection-product-card.liquid): "30% OFF" and "Free diamond studs".
  function cards() {
    document.querySelectorAll('#studio-edit .ds-discovery-card:not([data-ds-hc])').forEach(card => {
      const priceBox = card.querySelector('.ds-discovery-price');
      if (!priceBox) return;
      card.setAttribute('data-ds-hc', '');
      const now = toCents(priceBox.querySelector('strong')?.textContent);
      const was = toCents(priceBox.querySelector('del')?.textContent);
      const photo = card.querySelector('.ds-discovery-photo');
      const badges = [];
      if (now > 0 && was > now) {
        const pct = Math.round((1 - now / was) * 100);
        if (pct >= 5) badges.push('<span class="ds-hc-off">' + pct + '% OFF</span>');
      }
      if (qualifies(now)) badges.push('<span class="ds-hc-gift-badge" title="Orders of ' + esc(giftThresholdText()) + ' or more include free diamond stud earrings.">' + GIFT + 'Free diamond studs</span>');
      if (photo && badges.length) {
        const box = document.createElement('span');
        box.className = 'ds-hc-badges';
        box.innerHTML = badges.join('');
        photo.append(box);
      }
      const perks = document.createElement('p');
      perks.className = 'ds-hc-perks';
      perks.title = 'Every piece is made new for you. Estimated arrival about 2–2½ weeks from ordering (not a guarantee).';
      perks.textContent = eta();
      priceBox.after(perks);
    });
  }

  /* ---------- 5. Gift finder ---------- */
  function giftFinder() {
    const intro = document.querySelector('.gift-section .gift-intro');
    if (intro && giftOn() && !intro.querySelector('.ds-hc-gift-note')) {
      const note = document.createElement('p');
      note.className = 'ds-hc-gift-note';
      note.innerHTML = GIFT + '<span>Orders of ' + esc(giftThresholdText()) + ' or more include <strong>free diamond stud earrings</strong>. ' +
        '<button type="button" class="ds-hc-link" data-ds-stud-offer aria-haspopup="dialog" aria-controls="ds-stud-gift-dialog">Details</button></span>';
      note.querySelector('button').addEventListener('click', () => track('gift_details'));
      const select = intro.querySelector('#gift-budget');
      if (select) select.after(note); else intro.append(note);
    }
    document.querySelectorAll('#gift-picks .boutique-pick:not([data-ds-hc])').forEach(pick => {
      pick.setAttribute('data-ds-hc', '');
      lighten(pick.querySelector('img'), 300);
      const priceEl = pick.querySelector('b');
      if (priceEl && qualifies(toCents(priceEl.textContent))) priceEl.after(giftTag());
    });
  }

  /* ---------- 6. Customer clips ---------- */
  function clips() {
    const hint = document.querySelector('#customer-clips .ds-clips-hint');
    if (!hint || document.querySelector('.ds-hc-clips-cta')) return;
    const row = document.createElement('div');
    row.className = 'ds-hc-clips-cta';
    const badge = ratingButton('clips', r => '<strong>' + r.text + '</strong> &middot; Read all ' + r.count + ' ' + r.word);
    if (badge) row.append(badge);
    const link = document.createElement('a');
    link.className = 'btn btn-dark ds-hc-clips-cta__shop';
    link.href = root() + 'collections/lab-grown-diamond-rings';
    link.innerHTML = 'Shop engagement rings ' + ARROW;
    link.addEventListener('click', () => track('clips_shop_rings'));
    row.append(link);
    hint.after(row);
  }

  /* ---------- 6b. Written reviews next to the clips ---------- */
  // Real Judge.me store reviews already on the page (snippets/ds-store-rating.liquid); short 5-star ones, photos first.
  function reviews() {
    const cta = document.querySelector('#customer-clips .ds-hc-clips-cta');
    const tpl = document.getElementById('ds-store-rating-panel');
    if (!cta || !tpl || document.querySelector('.ds-hc-reviews')) return;
    const picks = [...tpl.content.querySelectorAll('.ds-sr-review')].map(item => {
      const stars = item.querySelector('.ds-sr-stars');
      return {
        name: (item.querySelector('.ds-sr-review__name')?.textContent || '').trim(),
        body: (item.querySelector('.ds-sr-review__body')?.textContent || '').replace(/\s+/g, ' ').trim(),
        rating: parseFloat(stars?.style.getPropertyValue('--ds-sr')) || 0,
        thumb: item.querySelector('.ds-sr-review__photo img')?.getAttribute('src') || ''
      };
    }).filter(r => r.name && r.rating >= 5 && r.body.length >= 100 && r.body.length <= 330)
      .sort((a, b) => (b.thumb ? 1 : 0) - (a.thumb ? 1 : 0))
      .slice(0, 3);
    if (picks.length < 3) return;
    const list = document.createElement('ul');
    list.className = 'ds-hc-reviews';
    list.setAttribute('role', 'list');
    list.setAttribute('aria-label', 'Customer reviews');
    list.innerHTML = picks.map(r => '<li class="ds-hc-review">' +
      '<span class="ds-sr-stars ds-sr-stars--sm" style="--ds-sr: ' + r.rating + '" role="img" aria-label="' + r.rating + ' out of 5 stars"></span>' +
      '<blockquote class="ds-hc-review__body"><p>' + esc(r.body) + '</p></blockquote>' +
      '<p class="ds-hc-review__by">' + (r.thumb ? '<img src="' + esc(r.thumb) + '" alt="" width="40" height="40" loading="lazy">' : '') + '<span>' + esc(r.name) + '</span></p>' +
      '</li>').join('');
    cta.before(list);
  }

  /* ---------- 7. Still in your bag ---------- */
  // First render uses the cart printed into the page by templates/index.liquid (#ds-hc-cart), so it appears
  // with the page instead of pushing it down later. After the bag drawer closes it re-reads /cart.js.
  let bagStarted = false;
  let bagWatch = false;
  let bagFirst = null;
  const isPiece = item => !/^option-set-/.test(item.handle || '') && !(item.properties && item.properties._gpo_parent_product_group) && item.final_line_price > 0;
  function renderBag(cart) {
    const services = document.querySelector('.store-services');
    document.querySelector('.ds-hc-bag')?.remove();
    const pieces = cart && Array.isArray(cart.items) ? cart.items.filter(isPiece) : [];
    bagFirst = null;
    if (!services || !pieces.length) return;
    const first = pieces[0];
    const more = pieces.reduce((n, item) => n + item.quantity, 0) - 1;
    let gift = '';
    try {
      const g = giftOn() && window.DSStudGiftCart?.compute ? window.DSStudGiftCart.compute(cart) : null;
      if (g && g.kind === 'qualified') gift = 'Your order qualifies for free diamond stud earrings';
      else if (g && g.goal > g.total) gift = money(g.goal - g.total) + ' away from free diamond stud earrings';
    } catch (e) { /* gift status unavailable */ }
    const src = typeof first.image === 'string' ? first.image : (first.image?.src || first.featured_image?.url || '');
    const img = src ? src + (src.includes('?') ? '&' : '?') + 'width=160' : '';
    bagFirst = {title: first.product_title, url: first.url, image: img, price: cart.total_price};
    const section = document.createElement('section');
    section.className = 'ds-hc-bag';
    section.setAttribute('aria-labelledby', 'ds-hc-bag-title');
    section.innerHTML = (img ? '<a class="ds-hc-bag__imglink" href="' + esc(first.url) + '" tabindex="-1" aria-hidden="true"><img class="ds-hc-bag__img" src="' + esc(img) + '" alt="" width="64" height="64"></a>' : '') +
      '<div class="ds-hc-bag__text"><p class="ds-hc-bag__eyebrow" id="ds-hc-bag-title">Still in your bag</p>' +
      '<p class="ds-hc-bag__title"><a href="' + esc(first.url) + '">' + esc(first.product_title) + '</a>' + (more > 0 ? '<span>+ ' + more + ' more</span>' : '') + '</p>' +
      '<p class="ds-hc-bag__meta"><strong>' + esc(money(cart.total_price)) + '</strong>' + (gift ? '<span class="ds-hc-bag__gift">' + GIFT + esc(gift) + '</span>' : '') + '</p></div>' +
      '<div class="ds-hc-bag__actions"><button type="button" class="btn btn-dark ds-hc-bag__view">View bag ' + ARROW + '</button>' +
      '<a class="text-link ds-hc-bag__checkout" href="' + esc(root() + 'checkout') + '">Checkout</a></div>';
    section.querySelector('.ds-hc-bag__view').addEventListener('click', () => {
      track('bag_view');
      if (window.DSCartDrawer?.open) window.DSCartDrawer.open(); else location.href = root() + 'cart';
    });
    section.querySelector('.ds-hc-bag__checkout').addEventListener('click', () => track('bag_checkout'));
    section.querySelectorAll('.ds-hc-bag__title a, .ds-hc-bag__imglink').forEach(a => a.addEventListener('click', () => track('bag_item')));
    services.after(section);
  }
  async function refreshBag() {
    let cart = null;
    try { cart = await fetch(root() + 'cart.js', {credentials: 'same-origin'}).then(r => r.ok ? r.json() : null); } catch (e) { cart = null; }
    if (cart) renderBag(cart);
  }
  function bag() {
    if (bagStarted || !document.querySelector('.store-services')) return;
    bagStarted = true;
    if (!bagWatch) {
      const drawer = document.getElementById('ds-cart-drawer');
      if (drawer) { bagWatch = true; drawer.addEventListener('close', refreshBag); }
    }
    const el = document.getElementById('ds-hc-cart');
    let cart = null;
    try { cart = el ? JSON.parse(el.textContent) : null; } catch (e) { cart = null; }
    if (cart) renderBag(cart); else refreshBag();
  }

  /* ---------- 8. Good to know before you make an offer ---------- */
  // Answers come from the store's own copy (offer form, delivery, insured shipping and returns wording).
  const FAQ = [
    ['How does an offer work?', 'Choose your options and name your price. If the studio accepts, you check out at that price.'],
    ['Do I pay to send one?', 'No. Sending an offer is free. You only pay if you check out.'],
    ['How will I hear back?', 'By email. Add your phone number to the offer if you would rather talk by phone.'],
    ['When will it arrive?', 'Every piece is made new for you and arrives in about 2–2½ weeks, with free insured shipping.'],
    ['What if it is not right?', 'You have 60 days to return any piece, including made-to-order and engraved ones.']
  ];
  function offerFaq() {
    const band = document.querySelector('.concierge-band');
    if (!band || document.querySelector('.ds-hc-faq')) return;
    const section = document.createElement('section');
    section.className = 'ds-hc-faq';
    section.setAttribute('aria-labelledby', 'ds-hc-faq-title');
    section.innerHTML = '<div class="ds-hc-faq__head"><div><span class="kicker">Questions, answered</span><h2 id="ds-hc-faq-title">Good to know before you make an offer</h2></div>' +
      '<a class="text-link ds-hc-faq__more" href="' + esc(root() + 'pages/diamond-jewelry-faq') + '">More questions? Read the FAQ ' + ARROW + '</a></div>' +
      '<dl class="ds-hc-faq__list">' + FAQ.map(([q, a]) => '<div class="ds-hc-faq__item"><dt>' + esc(q) + '</dt><dd>' + esc(a) + '</dd></div>').join('') + '</dl>';
    section.querySelector('.ds-hc-faq__more').addEventListener('click', () => track('faq_more'));
    band.before(section);
  }

  /* ---------- 9. Concierge band: a second way in ---------- */
  function concierge() {
    const band = document.querySelector('.concierge-band');
    const button = band?.querySelector('[data-open-finder]');
    if (!button || band.querySelector('.ds-hc-concierge')) return;
    const wrap = document.createElement('div');
    wrap.className = 'ds-hc-concierge';
    button.before(wrap);
    wrap.append(button);
    const link = document.createElement('a');
    link.className = 'text-link ds-hc-concierge__talk';
    link.href = root() + 'pages/contact-us';
    link.setAttribute('data-ds-consult', '');
    link.innerHTML = 'Or talk to the studio ' + ARROW;
    wrap.append(link);
    button.addEventListener('click', () => track('concierge_finder'));
    link.addEventListener('click', () => track('concierge_talk'));
  }

  /* ---------- 11. "Find my match" results ---------- */
  // ds-catalog-discovery.js writes three a.result-card links into #ds-finder-catalog. Each gets a "Make an offer"
  // row right under it (a sibling link, so links are not nested) and the gift tag, in the same microtask as the
  // render. The "% OFF" tag needs the compare-at price, so it is read from /products/<handle>.js and laid over
  // the photo (no layout shift).
  const saleRequests = new Map();
  function saleBadge(card) {
    const handle = (card.getAttribute('href') || '').match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle) return;
    if (!saleRequests.has(handle)) {
      saleRequests.set(handle, fetch(root() + 'products/' + handle + '.js', {credentials: 'same-origin'})
        .then(r => r.ok ? r.json() : null).catch(() => null));
    }
    saleRequests.get(handle).then(p => {
      if (!p || !card.isConnected || card.querySelector('.ds-hc-result-off')) return;
      const now = toCents(card.querySelector('b')?.textContent);
      const was = Number(p.compare_at_price) || 0;
      if (!now || was <= now) return;
      const pct = Math.round((1 - now / was) * 100);
      if (pct < 5) return;
      const badge = document.createElement('span');
      badge.className = 'ds-hc-off ds-hc-result-off';
      badge.textContent = pct + '% OFF';
      card.append(badge);
    });
  }
  function finderResults() {
    const list = document.getElementById('ds-finder-catalog');
    if (!list) return;
    list.querySelectorAll(':scope > a.result-card:not([data-ds-hc])').forEach(card => {
      card.setAttribute('data-ds-hc', '');
      lighten(card.querySelector('img'), 300);
      const href = card.getAttribute('href');
      const title = (card.querySelector('strong')?.textContent || '').trim();
      const price = card.querySelector('b');
      if (price && qualifies(toCents(price.textContent))) price.after(giftTag());
      if (href) {
        const offer = document.createElement('a');
        offer.className = 'ds-hc-result-offer';
        offer.href = offerUrl(href);
        offer.setAttribute('data-ds-hc-finder', 'offer');
        offer.setAttribute('aria-label', 'Make an offer on ' + title + '. No payment to send an offer.');
        offer.innerHTML = '<span><strong>Make an offer</strong><span class="ds-hc-result-offer__note">No payment to send one</span></span>' + ARROW;
        card.after(offer);
        saleBadge(card);
      }
    });
  }

  /* ---------- 12. Finder funnel measurement ---------- */
  let funnelBound = false;
  function finderFunnel() {
    if (funnelBound) return;
    funnelBound = true;
    const push = (action, extra) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({event: 'ds_home_finder', action}, extra || {}));
    };
    document.addEventListener('click', event => {
      const opener = event.target.closest('[data-open-finder]');
      if (opener) {
        const source = opener.closest('#hero') ? 'hero' : opener.closest('.concierge-band') ? 'concierge' :
          opener.closest('.gift-section') ? 'gift' : opener.closest('#ds-mobile-nav') ? 'mobile_menu' :
          opener.closest('header, .ds-header-root') ? 'header' : 'other';
        push('open', {source});
        return;
      }
      const link = event.target.closest('#finder-dialog a');
      if (!link) return;
      if (link.matches('[data-ds-hc-finder="offer"]')) push('offer');
      else if (link.classList.contains('result-card')) push('piece');
      else if (/\/collections\//.test(link.pathname)) push('see_all');
      else push('other_link');
    });
    document.addEventListener('ds:finder-results', event => {
      const d = event.detail || {};
      push('results', {status: d.status, result_count: d.resultCount, budget: d.budget, category: d.category, shape: d.shape});
    });
  }

  /* ---------- 10. Desktop: a gentle reminder when the pointer leaves the page ---------- */
  // Shares its once-per-session flag with the product page's "Name your price" card (ds-cta-cro.js),
  // so a shopper sees at most one of these per visit.
  const NUDGE_KEY = 'ds-cta-offer-nudge';
  function showNudge() {
    let kind, eyebrow, title, body, goLabel, go, piece = null;
    if (bagFirst) {
      kind = 'bag';
      eyebrow = 'Still in your bag';
      title = 'Ready when you are.';
      body = 'Your piece is saved in your bag.';
      piece = {title: bagFirst.title, image: bagFirst.image, url: bagFirst.url};
      goLabel = 'View bag';
      go = () => { if (window.DSCartDrawer?.open) window.DSCartDrawer.open(); else location.href = root() + 'cart'; };
    } else if (recentItems[0]) {
      const p = recentItems[0];
      const src = p.featured_image.startsWith('//') ? 'https:' + p.featured_image : p.featured_image;
      kind = 'recent';
      eyebrow = 'Before you go';
      title = 'Still thinking it over?';
      body = 'Name your price. If the studio accepts, you check out at that price.';
      piece = {title: p.title, image: src + (src.includes('?') ? '&' : '?') + 'width=160', url: p.url, price: money(p.price)};
      goLabel = 'Make an offer';
      go = () => { location.href = p.url + (p.url.includes('?') ? '&' : '?') + 'intent=offer'; };
    } else {
      kind = 'finder';
      eyebrow = 'Before you go';
      title = 'Not sure where to start?';
      body = 'Answer three quick questions and see pieces that match your moment, style and budget.';
      goLabel = 'Find my match';
      go = () => { document.querySelector('[data-open-finder]')?.click(); };
    }
    const card = document.createElement('aside');
    card.className = 'ds-cta-nudge ds-hc-nudge';
    card.setAttribute('aria-labelledby', 'ds-hc-nudge-title');
    card.innerHTML = '<button type="button" class="ds-cta-nudge__close" aria-label="Close">&times;</button>' +
      '<p class="ds-cta-nudge__eyebrow">' + esc(eyebrow) + '</p>' +
      '<p class="ds-cta-nudge__title" id="ds-hc-nudge-title">' + esc(title) + '</p>' +
      (piece ? '<a class="ds-hc-nudge__piece" href="' + esc(piece.url) + '">' + (piece.image ? '<img src="' + esc(piece.image) + '" alt="" width="56" height="56">' : '') +
        '<span><span class="ds-hc-nudge__name">' + esc(piece.title) + '</span>' + (piece.price ? '<span class="ds-hc-nudge__price">' + esc(piece.price) + '</span>' : '') + '</span></a>' : '') +
      '<p class="ds-cta-nudge__body">' + esc(body) + '</p>' +
      '<div class="ds-cta-nudge__actions"><button type="button" class="ds-cta-nudge__go">' + esc(goLabel) + '</button><button type="button" class="ds-cta-nudge__later">Not now</button></div>';
    const close = how => { card.remove(); document.removeEventListener('keydown', onKey); if (how) track('nudge_' + how, {kind}); };
    const onKey = event => { if (event.key === 'Escape') close('close'); };
    card.querySelector('.ds-cta-nudge__close').addEventListener('click', () => close('close'));
    card.querySelector('.ds-cta-nudge__later').addEventListener('click', () => close('later'));
    card.querySelector('.ds-cta-nudge__go').addEventListener('click', () => { close('go'); go(); });
    card.querySelector('.ds-hc-nudge__piece')?.addEventListener('click', () => track('nudge_piece', {kind}));
    document.addEventListener('keydown', onKey);
    document.body.append(card);
    track('nudge_show', {kind});
  }
  function exitNudge() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 900px)').matches) return;
    try { if (sessionStorage.getItem(NUDGE_KEY)) return; } catch (e) { return; }
    const started = Date.now();
    const onOut = event => {
      if (event.relatedTarget || event.clientY > 0 || Date.now() - started < 15000) return;
      if (document.visibilityState !== 'visible' || document.querySelector('dialog[open], .ds-cta-nudge')) return;
      document.removeEventListener('mouseout', onOut);
      try { sessionStorage.setItem(NUDGE_KEY, '1'); } catch (e) { /* storage unavailable */ }
      showNudge();
    };
    document.addEventListener('mouseout', onOut);
  }

  /* ---------- Start, and re-apply when ds-home.js re-renders ---------- */
  function apply() {
    hero();
    heroFinder();
    heroLabel();
    services();
    recent();
    cards();
    giftFinder();
    clips();
    bag();
    reviews();
    offerFaq();
    concierge();
    finderResults();
  }
  // Re-apply in the same task as the change (a microtask), so additions land before the browser paints.
  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; apply(); });
  };
  function start() {
    apply();
    new MutationObserver(queue).observe(document.body, {childList: true, subtree: true});
    finderFunnel();
    exitNudge();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
